import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { DesktopDialog, HubSheet } from "../AdminPortal/HubPwa";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { ActTarget } from "../../../activation";
import aa from "../AdminPortal/adminApp.module.css";
import L from "../../../constants/baysportLabels";
import { Button, Field, Select, useToast } from "../../ui";
import { useTrainingMode } from "../../../training";

export default function ClientAddMethodSheet({
  open,
  onClose,
  onManual,
  /** Тренер: ссылка только с привязкой к своей группе */
  requireGroup = false,
}) {
  const isMobile = useIsMobile(768);
  const toast = useToast();
  const { active: trainingActive, markLinkCopied } = useTrainingMode();
  const [view, setView] = useState("menu");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkGroupName, setLinkGroupName] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setView("menu");
      setGroupId("");
      setLinkUrl("");
      setLinkGroupName("");
      setLinkLoading(false);
      setGroupsLoading(false);
    }
  }, [open]);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const groupsRes = await clinicAPI.getSportGroupsSummary().catch(() =>
        clinicAPI.getServicesCatalog().then((c) => ({
          groups: c.specializations || [],
        })),
      );
      setGroups(groupsRes.groups || groupsRes.specializations || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить группы");
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, [toast]);

  const loadGeneralLink = useCallback(
    async (selectedGroupId = groupId) => {
      setLinkLoading(true);
      try {
        const r = await clinicAPI.getDefaultRegistrationLink(
          selectedGroupId || null,
        );
        const url = r.link?.url || "";
        if (!url) throw new Error("Ссылка недоступна");
        setLinkUrl(url);
        setLinkGroupName(r.link?.group?.name || "");
        return url;
      } catch (e) {
        toast.error(
          e.response?.data?.error || e.message || "Не удалось получить ссылку",
        );
        setLinkUrl("");
        setLinkGroupName("");
        return null;
      } finally {
        setLinkLoading(false);
      }
    },
    [groupId, toast],
  );

  const openGeneralLink = async () => {
    setView("link");
    setGroupsLoading(true);
    let list = groups;
    try {
      if (list.length === 0) {
        const groupsRes = await clinicAPI.getSportGroupsSummary().catch(() =>
          clinicAPI.getServicesCatalog().then((c) => ({
            groups: c.specializations || [],
          })),
        );
        list = groupsRes.groups || groupsRes.specializations || [];
        setGroups(list);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить группы");
      setView("menu");
      return;
    } finally {
      setGroupsLoading(false);
    }
    const initialGroupId =
      groupId || (requireGroup && list[0]?.id ? String(list[0].id) : "");
    if (requireGroup && !initialGroupId) {
      toast.error("Нет закреплённых групп — обратитесь к администратору");
      setView("menu");
      return;
    }
    if (initialGroupId && initialGroupId !== groupId) {
      setGroupId(initialGroupId);
    }
    await loadGeneralLink(initialGroupId);
  };

  const onGroupChange = async (e) => {
    const nextId = e.target.value;
    setGroupId(nextId);
    await loadGeneralLink(nextId);
  };

  const copyGeneralLink = async (url = linkUrl) => {
    const target = url || (await loadGeneralLink(groupId));
    if (!target) return;
    try {
      await navigator.clipboard.writeText(target);
      if (trainingActive) markLinkCopied?.();
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать ссылку");
    }
  };

  const pickManual = () => {
    onClose?.();
    onManual?.();
  };

  const groupHint = linkGroupName
    ? `После подтверждения заявки клиент попадёт в группу «${linkGroupName}».`
    : requireGroup
      ? "Выберите одну из ваших групп."
      : "Группа не выбрана — клиент не будет автоматически добавлен в группу.";

  const title = view === "link" ? "Ссылка для записи" : "Добавить клиента";
  const subtitle =
    view === "link"
      ? requireGroup
        ? "Клиент сам заполнит заявку в выбранную группу"
        : "Клиент сам заполнит заявку — семья или один человек"
      : "Вручную или по ссылке";

  const body =
    view === "menu" ? (
      <ActTarget stepId="client" subId="client_explore_add_methods" as="div">
      <div className={aa.clientAddMethodList}>
        <button type="button" className={aa.clientAddMethodBtn} onClick={pickManual}>
          <span className={aa.clientAddMethodIcon} aria-hidden>
            ✏️
          </span>
          <span className={aa.clientAddMethodText}>
            <strong>Вручную</strong>
            <small>Один клиент или семья (родитель + дети)</small>
          </span>
          <span className={aa.clientAddMethodChevron} aria-hidden>
            ›
          </span>
        </button>

        <button
          type="button"
          className={aa.clientAddMethodBtn}
          onClick={openGeneralLink}
          disabled={linkLoading}
        >
          <span className={aa.clientAddMethodIcon} aria-hidden>
            🔗
          </span>
          <span className={aa.clientAddMethodText}>
            <strong>{requireGroup ? "Ссылка в группу" : "Общая ссылка"}</strong>
            <small>
              {requireGroup
                ? "Клиент сам заполнит заявку в вашу группу"
                : "Клиент сам заполнит заявку — вы подтвердите в «На модерации»"}
            </small>
          </span>
          <span className={aa.clientAddMethodChevron} aria-hidden>
            ›
          </span>
        </button>
      </div>
      </ActTarget>
    ) : (
      <div className={aa.clientAddMethodLinkPane}>
        <button
          type="button"
          className={aa.clientAddMethodBack}
          onClick={() => setView("menu")}
        >
          ← Назад
        </button>

        <Field
          label={L.group}
          hint={
            requireGroup
              ? "Только ваши группы"
              : "Можно выбрать группу — клиент автоматически попадёт в неё после модерации"
          }
        >
          <Select
            value={groupId}
            onChange={onGroupChange}
            disabled={groupsLoading || linkLoading}
            required={requireGroup}
          >
            {!requireGroup ? (
              <option value="">Без привязки к группе</option>
            ) : (
              <option value="">Выберите группу</option>
            )}
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Select>
        </Field>

        {linkLoading ? (
          <p className={aa.clientAddMethodLinkHint}>Загрузка ссылки…</p>
        ) : linkUrl ? (
          <>
            <p className={aa.clientAddMethodLinkHint}>
              Отправьте ссылку клиенту. Он выберет «семья» или «один человек» и
              заполнит заявку. После регистрации она появится в «На модерации»
              (если автоподтверждение выключено). {groupHint}
            </p>
            <div className={aa.clientAddMethodLinkBox}>{linkUrl}</div>
            <Button type="button" onClick={() => copyGeneralLink()}>
              Копировать ссылку
            </Button>
          </>
        ) : (
          <Button type="button" variant="secondary" onClick={() => loadGeneralLink()}>
            Получить ссылку
          </Button>
        )}
      </div>
    );

  if (isMobile) {
    return (
      <HubSheet
        open={open}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        titleId="client-add-method-title"
        bodyClassName={aa.clientAddMethodBody}
      >
        {body}
      </HubSheet>
    );
  }

  return (
    <DesktopDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      titleId="client-add-method-title"
      wide
      bodyClassName={aa.clientAddMethodBody}
    >
      {body}
    </DesktopDialog>
  );
}
